import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { groupArrayBy } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  deserializeAppliedFilters,
  type PropertyFilterInfoForQueryFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import objectUtils from 'donor-elf-web/lib/object-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import CampaignPledgeModel from 'donor-elf-web/models/campaign-pledge';
import LineItemModel from 'donor-elf-web/models/line-item';
import PledgeModel from 'donor-elf-web/models/pledge';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import { validateCampaignLineItem } from 'donor-elf-web/validations/campaign-line-item';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import moment from 'moment';

const PAGE_SIZE = 250;

export class ByTransactionRow {
  constructor(args: {
    lineItem: LineItemModel;
    campaignLineItemChangeset: any;
    pledgeInfos?: PledgeAndCampaignPledge[];
  }) {
    this.lineItem = args.lineItem;
    this.campaignLineItemChangeset = args.campaignLineItemChangeset;
    this.pledgeInfos = args.pledgeInfos || [];
  }

  lineItem: LineItemModel;
  @tracked selected = false;

  // the properties below are used for the <LineItem::ApproveCamapign /> component
  // we can't put @tracked properties directly on the component because <EmberTable />
  // will re-use the same component for different rows
  @tracked campaignLineItemChangeset: any;
  @tracked pledgeInfos: PledgeAndCampaignPledge[];
  @tracked showValidationErrors = false;
  @tracked approving = false;
  @tracked approveError?: any;

  get approved() {
    return (
      !this.approving &&
      this.campaignLineItemChangeset?.get('approved') === true
    );
  }
}

interface ByTransactionTableColumn {
  name: string;
  isFixed?: string;
  width?: number;
  isCampaignColumn?: boolean;
  isContactColumn?: boolean;
  isTransactionColumn?: boolean;
}

class ByContactRow {
  constructor(args: {
    contactId: string;
    pledgeInfos: PledgeAndCampaignPledge[];
    lineItemInfos: ByContactRowLineItemInfo[];
  }) {
    this.contactId = args.contactId;
    this.pledgeInfos = args.pledgeInfos;
    this.lineItemInfos = args.lineItemInfos;
  }

  contactId: string;
  @tracked pledgeInfos: PledgeAndCampaignPledge[];
  @tracked lineItemInfos: ByContactRowLineItemInfo[];

  get allLineItemInfosSelected() {
    return this.lineItemInfos.every((x) => x.selected);
  }

  // amount from most recent line item
  get newPledgeAmount() {
    return this.lineItemInfos[0]?.lineItem?.amount;
  }

  // date from earliest line item
  get newPledgeStartDate() {
    return this.lineItemInfos[this.lineItemInfos.length - 1]?.lineItem
      ?.transDate;
  }
}

class ByContactRowLineItemInfo {
  constructor(args: { lineItem: LineItemModel }) {
    this.lineItem = args.lineItem;
  }

  lineItem: LineItemModel;
  @tracked selected: boolean = false;

  get approved() {
    return this.lineItem.campaignLineItemsArray[0]?.approved === true;
  }
}

interface ByContactTableColumn {
  name: string;
  isFixed?: string;
  width?: number;
  isContactColumn?: boolean;
  isPledgesColumn?: boolean;
  isTransactionsColumn?: boolean;
}

class PledgeAndCampaignPledge {
  constructor(args: {
    pledge: PledgeModel;
    campaignPledge?: CampaignPledgeModel;
  }) {
    this.pledge = args.pledge;
    this.campaignPledge = args.campaignPledge;
  }

  pledge: PledgeModel;
  @tracked campaignPledge?: CampaignPledgeModel;
}

type GroupBy = 'transaction' | 'contact';

export default class CampaignsApproveTransactionsController extends Controller {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare fundGoalService: FundGoalService;
  @service declare media: any;
  @service declare store: Store;

  queryParams = ['filters', 'groupBy'];

  @tracked filters?: string;
  @tracked groupBy: GroupBy = 'contact';
  @tracked page = 1;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked loading = true;
  @tracked loadingMore = false;
  @tracked byTransactionRows: ByTransactionRow[] = [];
  @tracked byTransactionTableColumns: ByTransactionTableColumn[] = [];
  @tracked byContactRows: ByContactRow[] = [];
  @tracked byContactTableColumns: ByContactTableColumn[] = [];

  @tracked lineItems: LineItemModel[] = [];
  @tracked pledges: PledgeModel[] = [];
  @tracked campaignPledges: CampaignPledgeModel[] = [];

  @tracked lineItemsToAssignToCampaign?: LineItemModel[];

  savePledgeSubsciptionToken?: string;

  get leftToApproveCount() {
    return this.lineItems.filter(
      (x) => x.campaignLineItemsArray[0]?.approved !== true,
    ).length;
  }

  load = async () => {
    this.loading = true;
    await this.campaignService.load();

    let queryFilter: any = {
      offset: 0,
      limit: PAGE_SIZE,
      exclude_meta_total_amount: true,
    };
    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }

    this.lineItems = await queryWithPages<LineItemModel>({
      store: this.store,
      modelName: 'line-item',
      queryOptions: { filter: objectUtils.decamelizeKeys(queryFilter) },
      pageSize: PAGE_SIZE,
    });

    this.pledges = [];
    this.campaignPledges = [];
    if (this.lineItems.length > 0) {
      let earliestTransDate = this.lineItems.reduce((earliest, record) => {
        return earliest.transDate <= record.transDate ? earliest : record;
      }).transDate;

      this.pledges = await queryWithPages<PledgeModel>({
        store: this.store,
        modelName: 'pledge',
        queryOptions: {
          filter: {
            start_date: moment(earliestTransDate).format('YYYY-MM-DD'),
            is_active: true,
          },
        },
        pageSize: PAGE_SIZE,
      });

      this.campaignPledges = await queryWithPages<CampaignPledgeModel>({
        store: this.store,
        modelName: 'campaign-pledge',
        queryOptions: {
          filter: {
            pledge_start_date: moment(earliestTransDate).format('YYYY-MM-DD'),
            is_active: true,
          },
        },
        pageSize: PAGE_SIZE,
      });
    }

    if (this.groupBy === 'transaction') {
      this.setByTransactionRows();
    } else {
      this.setByContactRows();
    }
    this.loading = false;
  };

  setByTransactionRows() {
    let rows: ByTransactionRow[] = [];
    let {
      campaignLineItemValidation,
      pledgesByContactId,
      campaignPledgesByPledgeId,
    } = this;
    for (let lineItem of this.lineItems) {
      let campaignLineItem =
        lineItem.campaignLineItemsArray?.[0] ||
        this.store.createRecord('campaign-line-item', {
          lineItemId: lineItem.id,
          amount: lineItem.amount,
          approved: false,
        });
      let campaignLineItemChangeset = new Changeset(
        campaignLineItem,
        lookupValidator(campaignLineItemValidation),
        campaignLineItemValidation,
      ) as any;
      let pledges = pledgesByContactId[lineItem.contactId!] || [];
      // if the line item isn't associated with a campaign, and there's only one
      // pledge with campaignId, set the campaignId to the pledge's campaignId
      // as long as it's not a restricted campaign
      if (campaignLineItem.campaignId == null && pledges.length === 1) {
        let campaignPledge = campaignPledgesByPledgeId[pledges[0].id]?.[0];
        if (
          campaignPledge &&
          !this.campaignService.campaignsById[campaignPledge.campaignId]
            .restricted
        ) {
          campaignLineItemChangeset.set(
            'campaignId',
            campaignPledge.campaignId,
          );
        }
      }

      rows.push(
        new ByTransactionRow({
          lineItem,
          campaignLineItemChangeset,
          pledgeInfos: pledges.map((pledge) => {
            return new PledgeAndCampaignPledge({
              pledge,
              campaignPledge: campaignPledgesByPledgeId[pledge.id]?.[0],
            });
          }),
        }),
      );
    }

    this.byTransactionRows = rows;
  }

  setByContactRows() {
    let rows: ByContactRow[] = [];
    let {
      pledgesByContactId,
      campaignPledgesByPledgeId,
      lineItemsByContactId,
    } = this;

    for (let contactId in lineItemsByContactId) {
      let lineItems = lineItemsByContactId[contactId];
      let pledges = pledgesByContactId[contactId] || [];
      let pledgeInfos = pledges.map((pledge) => {
        return new PledgeAndCampaignPledge({
          pledge,
          campaignPledge: campaignPledgesByPledgeId[pledge.id]?.[0],
        });
      });

      rows.push(
        new ByContactRow({
          contactId,
          pledgeInfos,
          lineItemInfos: lineItems.map((lineItem) => {
            return new ByContactRowLineItemInfo({
              lineItem,
            });
          }),
        }),
      );
    }

    this.byContactRows = rows;
  }

  get campaignPledgesByPledgeId() {
    return groupArrayBy(
      this.campaignPledges,
      (campaignPledge) => campaignPledge.pledgeId!,
    );
  }

  get pledgesByContactId() {
    return groupArrayBy(this.pledges, (pledge) => pledge.contactId!);
  }

  get lineItemsByContactId() {
    return groupArrayBy(this.lineItems, (lineItem) => lineItem.contactId!);
  }

  @cached
  get campaignSelectOptions(): SelectOption[] {
    return this.campaignService.sortedNonRestrictedCampaigns.map((campaign) => {
      return { val: campaign.id, label: campaign.label };
    });
  }

  @cached
  get campaignLineItemValidation() {
    return validateCampaignLineItem({ campaignRequired: true });
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];
    list.push({
      propertyName: 'transDate',
      label: 'Date',
      propertyType: 'date',
      availableOperations: ['><', '>=', '<=', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.to = appliedFilter.val;
        } else if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        } else {
          queryFilter.date = appliedFilter.val;
        }
      },
    });
    list.push({
      propertyName: 'campaignNotApproved',
      label: 'Not Approved',
      propertyType: 'no-operation',
      applyToQueryFilter: async (
        _appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.campaignNotApproved = true;
      },
    });
    return list;
  }

  setTableColumns() {
    this.byTransactionTableColumns = [
      {
        name: 'Campaign',
        isFixed: 'left',
        width: 50,
        isCampaignColumn: true,
      },
      {
        name: 'Contact',
        width: this.media.isSmallDown ? 100 : 100,
        isContactColumn: true,
      },
      {
        name: 'Transaction',
        width: 200,
        isTransactionColumn: true,
      },
    ];

    this.byContactTableColumns = [
      {
        name: 'Contact',
        isFixed: 'left',
        width: 50,
        isContactColumn: true,
      },
      {
        name: 'Pledges',
        width: 100,
        isPledgesColumn: true,
      },
      {
        name: 'Transactions',
        width: 200,
        isTransactionsColumn: true,
      },
    ];
  }

  get defaultDateRangeAppliedFilter(): AppliedPropertyFilter {
    let from = this.fundGoalService.hasYtdGoalType
      ? this.fundGoalService.ytdStartMoment.format('YYYY-MM-DD')
      : moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD');

    return {
      propertyName: 'transDate',
      operation: '>=',
      val: from,
    };
  }

  get notApprovedAppliedFilter(): AppliedPropertyFilter {
    return {
      propertyName: 'campaignNotApproved',
    };
  }

  get showAssignToCampaignModal() {
    return (this.lineItemsToAssignToCampaign || []).length > 0;
  }

  templateDidInsert = () => {
    this.setTableColumns();
    if (this.savePledgeSubsciptionToken == null) {
      this.savePledgeSubsciptionToken =
        this.addSomethingModal.subscribeToPledgeSaved(
          this.handleAddSomethingPledgeSaved,
        );
    }
    if (isEmpty(this.filters)) {
      this.filters = serializeAppliedFilters([
        this.defaultDateRangeAppliedFilter,
        this.notApprovedAppliedFilter,
      ]);
    }
    this.deserializeFilters();
    this.load();
  };

  templateWillDestroy = () => {
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
    this.loading = true;
    this.loadingMore = false;
    this.filters = undefined;
    this.page = 1;
    this.appliedFilters = undefined;
    this.byTransactionRows = [];
    this.byTransactionTableColumns = [];
    this.lineItemsToAssignToCampaign = undefined;
  };

  adjustTableContainerHeight = (el: HTMLElement) => {
    let newHeight =
      window.innerHeight -
      (el.getBoundingClientRect().top + window.scrollY) -
      40;
    el.style.height = `${newHeight}px`;
  };

  handleFiltersChanged = (currentFilters: AppliedPropertyFilter[]) => {
    // only allow one dateRange filter
    if (
      currentFilters.filter((x) => x.propertyName === 'transDate').length > 1
    ) {
      currentFilters = [
        currentFilters.find((x) => x.propertyName === 'transDate')!,
        ...currentFilters.filter((x) => x.propertyName !== 'transDate'),
      ];
    }

    // confirm a dateRange filter is present
    if (!currentFilters.some((x) => x.propertyName === 'transDate')) {
      currentFilters.push(this.defaultDateRangeAppliedFilter);
    }

    if (
      !currentFilters.some(
        (x) => x.propertyName === this.notApprovedAppliedFilter.propertyName,
      )
    ) {
      currentFilters.push(this.notApprovedAppliedFilter);
    }

    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  };

  handleAddSomethingPledgeSaved = async (
    _msg: string,
    data: PledgeSavedArgs,
  ) => {
    let contactLineItems = this.lineItems.filter(
      (x) => x.contactId === data.pledge.contactId,
    );
    await this.store.query('lineItem', {
      filter: { ids: contactLineItems.map((x) => x.id) },
    });

    let contactRows = this.byTransactionRows.filter(
      (r) => r.lineItem.contactId === data.pledge.contactId,
    );
    if (data.newPledge) {
      for (let row of contactRows) {
        // if the pledge is associated with a campaign but the line item isn't
        // associated with a campaign, then set the campaignId to the pledge's
        // campaignId
        if (
          data.campaignPledge &&
          !this.campaignService.campaignsById[data.campaignPledge.campaignId]
            .restricted &&
          row.campaignLineItemChangeset.get('campaignId') == null
        ) {
          row.campaignLineItemChangeset.set(
            'campaignId',
            data.campaignPledge.campaignId,
          );
        }
        row.pledgeInfos = [
          ...row.pledgeInfos,
          new PledgeAndCampaignPledge({
            pledge: data.pledge,
            campaignPledge: data.campaignPledge,
          }),
        ];
      }
    } else if (data.deleted) {
      for (let row of contactRows) {
        row.pledgeInfos = row.pledgeInfos.filter(
          (x) => x.pledge.id !== data.pledge.id,
        );
      }
    } else {
      for (let row of contactRows) {
        let pledgeInfo = row.pledgeInfos.find(
          (x) => x.pledge.id === data.pledge.id,
        );
        if (pledgeInfo?.campaignPledge !== data.campaignPledge) {
          pledgeInfo!.campaignPledge = data.campaignPledge;
        }
        // if the pledge is associated with a campaign but the line item isn't
        // associated with a campaign, then set the campaignId to the pledge's
        // campaignId
        if (
          data.campaignPledge &&
          !this.campaignService.campaignsById[data.campaignPledge.campaignId]
            .restricted &&
          row.campaignLineItemChangeset.get('campaignId') == null
        ) {
          row.campaignLineItemChangeset.set(
            'campaignId',
            data.campaignPledge.campaignId,
          );
        }
      }
    }

    // byContactRows
    let contactRow = this.byContactRows.find(
      (r) => r.contactId === data.pledge.contactId,
    );
    if (contactRow) {
      if (data.newPledge) {
        contactRow.pledgeInfos = [
          ...contactRow.pledgeInfos,
          new PledgeAndCampaignPledge({
            pledge: data.pledge,
            campaignPledge: data.campaignPledge,
          }),
        ];
      } else if (data.deleted) {
        contactRow.pledgeInfos = contactRow.pledgeInfos.filter(
          (x) => x.pledge.id !== data.pledge.id,
        );
      } else {
        let pledgeInfo = contactRow.pledgeInfos.find(
          (x) => x.pledge.id === data.pledge.id,
        );
        if (pledgeInfo?.campaignPledge !== data.campaignPledge) {
          pledgeInfo!.campaignPledge = data.campaignPledge;
        }
      }
    }
  };

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  changeGroupBy = (groupBy: GroupBy) => {
    this.groupBy = groupBy;
    if (this.groupBy === 'contact' && this.byContactRows.length === 0) {
      this.setByContactRows();
    } else if (
      this.groupBy === 'transaction' &&
      this.byTransactionRows.length === 0
    ) {
      this.setByTransactionRows();
    }
  };

  toggleByContactAllLineItemsSelected = (info: ByContactRow, e: any) => {
    let selected = !e.target.checked;
    for (let lineItemInfo of info.lineItemInfos) {
      lineItemInfo.selected = !selected;
    }
  };

  toggleByContactLineItemInfoSelected = (info: ByContactRowLineItemInfo) => {
    info.selected = !info.selected;
  };

  handleAssignSelectedToCampaign = async (info: ByContactRow) => {
    if (info.lineItemInfos.every((x) => !x.selected)) {
      alert('Please select at least one transaction');
    }
    this.lineItemsToAssignToCampaign = info.lineItemInfos
      .filter((x) => x.selected)
      .map((x) => x.lineItem);
  };

  handleSavedAssignToCampaign = () => {
    this.lineItemsToAssignToCampaign = undefined;
  };

  handleCloseAssignToCampaign = () => {
    this.lineItemsToAssignToCampaign = undefined;
  };
}
